// routes
import Router from './routes';
// locales
import ThemeLocalization from './locales';
// Analytics
import { useEffect } from 'react';
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';
import { getE2eFeatureFlagOverrides } from 'featureFlags/e2eFeatureFlagOverrides';
// components
import { SnackbarProvider, ThemeProvider } from '@planitgeo/treeplotter-ui-library';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorPage, onError } from 'common/presentation/ErrorPage';
import { VITE_ENV } from 'config';
import { CookieManagementProvider } from 'common/infrastructure/cookies';
import PostHogRouteTracker from 'common/infrastructure/cookies/PostHogRouteTracker';
import TenantSwitchNotification from 'components/tenant-switch-notification/TenantSwitchNotification';
import FullscreenProvider from 'components/fullscreen/FullscreenProvider';
import TenantSwitchDialog from 'components/tenant-switch-dialog/TenantSwitchDialog';
import WorkTaskSyncRunner from 'components/work/WorkTaskSyncRunner';
import QueuedSubmissionFailureToaster from 'common/presentation/QueuedSubmissionFailureToaster';
// ----------------------------------------------------------------------

export default function App() {
  useEffect(() => {
    // PostHog only refetches flags on explicit triggers, never on a timer.
    // Reload on tab/app foreground so a flag changed in PostHog while this
    // tab was backgrounded doesn't require a manual cache/cookie clear.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        posthog.reloadFeatureFlags();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // e2e/QA only: force flag values via bootstrap when the override key is present in
  // localStorage. Inert for real users (no key). See the util for why bootstrap is needed.
  const e2eBootstrapFlags = getE2eFeatureFlagOverrides();
  return (
    <CookieManagementProvider url={VITE_ENV.cookieYesUrl}>
      <PostHogProvider
        apiKey={VITE_ENV.posthog.key}
        options={{
          api_host: VITE_ENV.posthog.host,
          opt_out_capturing_by_default: true,
          // Layer 1 guard: hard-disable analytics capture at init in regions
          // where analytics is off (GDPR — US only). Feature flags are NOT
          // disabled (no advanced_disable_feature_flags) so they work
          // everywhere; only behavioural capture is gated.
          autocapture: VITE_ENV.posthog.analyticsEnabled,
          // Pageviews are captured by PostHogRouteTracker, not by posthog-js.
          // Its 'history_change' extension patches pushState and captures
          // synchronously, before React effects run, so the masked
          // `route_pattern` could never be registered in time — every pageview
          // would carry the previous route's pattern. Leaving this false keeps
          // the tracker the single source of pageviews (no double counting);
          // the legacy `true` is also wrong here, firing only on initial load.
          capture_pageview: false,
          capture_pageleave: VITE_ENV.posthog.analyticsEnabled,
          disable_session_recording: !VITE_ENV.posthog.analyticsEnabled,
          person_profiles: 'identified_only',
          ...(e2eBootstrapFlags
            ? {
                bootstrap: { featureFlags: e2eBootstrapFlags },
                // Overridden sessions must stay pinned: block every /flags fetch
                // (including reloadFeatureFlags() on cookie-consent changes) so a
                // late response can't clobber the bootstrapped values.
                advanced_disable_feature_flags: true,
              }
            : {}),
        }}
      >
        <ThemeProvider>
          <ThemeLocalization>
            <SnackbarProvider>
              <PostHogRouteTracker />
              <FullscreenProvider />
              <TenantSwitchNotification />
              <TenantSwitchDialog />
              <WorkTaskSyncRunner />
              <QueuedSubmissionFailureToaster />
              <ErrorBoundary FallbackComponent={ErrorPage} onError={onError}>
                <Router />
              </ErrorBoundary>
            </SnackbarProvider>
          </ThemeLocalization>
        </ThemeProvider>
      </PostHogProvider>
    </CookieManagementProvider>
  );
}
